import React, {useLayoutEffect, useMemo} from 'react'
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import EntitiesTableProvider from 'components/organisms/Providers/TableProvider/EntitiesTableProvider';
import {useEffectEvent, useEffectItem, useProviderView} from 'helpers/hooks/utils';
import {useEntitySearch} from 'services/entity/entity.hooks';
import {
  useDatabaseEntityColumns,
  useDatabaseEntityFilterGroups,
  useDatabaseEntitySelected
} from 'services/entity/entity.utils';
import {useClientGet} from 'services/client/client.hooks';
import {useAuthClientId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import utils from 'helpers/utils';
import searchUtils from 'helpers/search';
import {useCollectionGet} from 'services/collection/collection.hooks';
import {useClientCustomFieldCache} from 'services/client/customField/customField.hooks';
import {useViewList} from 'services/view/view.hooks';

const DatabaseEntitiesTableLoader = ({children, active}) => {
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState());

  const queryMemo = useMemo(() => {
    if (listState.query) {
      return searchUtils.cleanQuery({...listState.query});
    } else {
      return null;
    }
  }, [listState.query]);

  const list = useEntitySearch({
    clientId: tableProvider.context?.data?.clientId,
    query: queryMemo,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.infinite,
    enablePreload: true,
    enabled: (active && listState.active) && utils.isDefined(queryMemo) && tableProvider.context?.data?.clientId >= 0
  });

  const setListEvent = useEffectEvent(tableProvider.setList);
  useLayoutEffect(() => {
    setListEvent?.(list);
  }, [setListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const DatabaseEntitiesTableProvider = (props) => {
  const clientId = useAuthClientId();

  const client = useClientGet({clientId}, {enabled: Boolean(!props.context && clientId >= 0)});

  const collection = useCollectionGet({collectionId: client?.data?.universeCollectionId},
    {enabled: Boolean(client?.data?.universeCollectionId)});

  const views = useViewList({
    relationType: 'database',
    relationId: 0,
    subscribed: true,
    allPages: true
  }, {
    enabled: utils.isDefined(clientId)
  });
  const view = useProviderView('database.entity', views?.data, props.skipView);

  const columns = useDatabaseEntityColumns(view);
  const customFields = useClientCustomFieldCache({clientId: clientId},
    {enabled: clientId >= 0});
  const customFieldsCache = (customFields.data?.data ?? customFields.data);
  const filterGroups = useDatabaseEntityFilterGroups(view, collection?.data, customFieldsCache, clientId);

  // isLoading anything, is loading something definition related
  const isLoading = props?.isLoading || client.status?.isLoading || customFields?.status?.isLoading || collection.status?.isLoading || views.status?.isLoading;
  const isDefinitionsLoading = props?.isDefinitionsLoading || !customFields?.status?.isSuccess || !collection.status?.isSuccess;

  const columnDefinitions = useMemo(() => {
    return columns?.length > 0 ? columns : null;
  }, [columns]);

  const filterGroupDefinitions = useMemo(() => {
    return filterGroups?.length > 0 ? filterGroups : null;
  }, [filterGroups]);

  const options = useMemo(() => ({
    listState: {
      initial: {
        pageSize: 25
      },
      options: {
        type: constants.appState.type.local,
        name: `databaseEntities`,
        searchParams: true
      }
    },
    testListState: {
      initial: {
        active: false,
        pageSize: 0
      },
      options: {
        name: `databaseEntities_test`
      }
    },
    listSelection: {
      options: {
        max: constants.selection.max.database
      }
    },
    columnState: {
      initial: {},
      options: {
        name: `databaseEntities`,
        type: constants.appState.type.local
      }
    },
    graphState: {
      initial: {},
      options: {
        name: `databaseEntities`,
        type: constants.appState.type.local
      }
    },
    assistantState: {
      initial: {},
      options: {
        name: `databaseEntities`,
        type: constants.appState.type.local
      }
    },
    columnDefinitions,
    filterGroupDefinitions
  }), [columnDefinitions, filterGroupDefinitions]);

  const fieldData = useMemo(() => {
    return {};
  }, []);

  const selectedItems = useDatabaseEntitySelected(clientId);
  const updaters = useMemo(() => {
    return {
      selectedItems: selectedItems,
    };
  }, [selectedItems]);

  return <EntitiesTableProvider {...props}
                                view={view}
                                options={options}
                                context={props.context ?? client}
                                fieldData={fieldData}
                                updaters={updaters}
                                views={views}
                                LoaderComponent={DatabaseEntitiesTableLoader}
                                isDefinitionsLoading={isDefinitionsLoading}
                                isLoading={isLoading}>
    {props.children}
  </EntitiesTableProvider>
};

DatabaseEntitiesTableProvider.propTypes = {
}

export default DatabaseEntitiesTableProvider;
